import { jsx, Global } from '@emotion/core';
import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import { useLocalization } from '../../../../localization';
import * as translations from '../../localization';
import DayPicker, { DateUtils } from 'react-day-picker';
import 'react-day-picker/lib/style.css'; // @ts-ignore
// eslint-disable-next-line @typescript-eslint/camelcase

import { calendar_16, calendarRangeArrow_16 } from '@trustpilot/icons';
import { Button } from '../../../Button';
import { Icon } from '../../../Icon';
import { Input } from '../../../Input/input';
import { Button as FilterButton } from '../button';
import { useToggle } from '../../../shared';
import { Link } from '../link';
import Dropdown from './dateDropdown';
import { NavBar } from './navbar';
import { PresetSelector } from './presetSelector';
import { Wrapper, CalendarContainer, InputsContainer, InputFieldError } from './styled';
import { StyleOverrides } from './StyleOverrides';
import { formatDate, dateFromDateString, sanitizeDateString, validateDate, autoCompleteDateString } from './utils';

function nullToUndefined(date) {
  if (!date) {
    return undefined; // eslint-disable-line
  }

  return date;
}
/* eslint-disable @typescript-eslint/camelcase */


var DropdownHandle = function DropdownHandle(_a) {
  var toggled = _a.toggled,
      dateCount = _a.dateCount,
      toggle = _a.toggle,
      title = _a.title,
      id = _a.id;
  return jsx(FilterButton, {
    icon: calendar_16,
    iconClassName: 'daterange-dropdown-icon',
    onClick: toggle,
    active: dateCount > 0 || toggled,
    id: id
  }, title);
};

var DropdownFooter = function DropdownFooter(_a) {
  var apply = _a.apply,
      dateRange = _a.dateRange,
      strings = _a.strings,
      hasFilterBehaviour = _a.hasFilterBehaviour,
      clear = _a.clear,
      id = _a.id,
      disabled = _a.disabled;
  return jsx(React.Fragment, null, jsx(Button, {
    "data-testid": "pl-header-filters-" + id + "-apply",
    disabled: disabled,
    size: "small",
    onClick: apply
  }, strings.select.apply), hasFilterBehaviour && dateRange.startDate && dateRange.endDate && jsx(Link, {
    id: "pl-header-filters-" + id + "-clear",
    secondary: true,
    inline: true,
    onClick: clear
  }, strings.select.clear));
};

function printDate(date, locale) {
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  });
}

var DateRange = React.forwardRef(function (_a, ref) {
  var id = _a.id,
      label = _a.label,
      _b = _a.startDate,
      startDate = _b === void 0 ? null : _b,
      _c = _a.endDate,
      endDate = _c === void 0 ? null : _c,
      onChange = _a.onChange,
      _d = _a.hasFilterBehavior,
      hasFilterBehavior = _d === void 0 ? true : _d,
      _e = _a.presets,
      presets = _e === void 0 ? [] : _e,
      updateCount = _a.updateCount,
      hidden = _a.hidden;

  var _f = useLocalization(translations),
      strings = _f.strings,
      language = _f.language;

  var fromDateRef = useRef(null);

  var _g = React.useState(0),
      _count = _g[0],
      setCount = _g[1];

  var _h = useToggle(false),
      toggled = _h[0],
      toggle = _h[1];

  var _j = useState(window.innerWidth < 416),
      compact = _j[0],
      setCompact = _j[1];

  var _k = useState(startDate),
      from = _k[0],
      updateFrom = _k[1];

  var _l = useState(startDate ? formatDate(startDate) : ''),
      fromInputField = _l[0],
      updateFromInputField = _l[1];

  var _m = useState(''),
      fromInputFieldError = _m[0],
      updateFromInputFieldError = _m[1];

  var _o = useState(endDate),
      to = _o[0],
      updateTo = _o[1];

  var _p = useState(endDate),
      enteredTo = _p[0],
      updateEnteredTo = _p[1];

  var _q = useState(endDate ? formatDate(endDate) : ''),
      toInputField = _q[0],
      updateToInputField = _q[1];

  var _r = useState(''),
      toInputFieldError = _r[0],
      updateToInputFieldError = _r[1];

  var _s = useState(false),
      presetUsed = _s[0],
      updatePresetUsed = _s[1];

  useEffect(function () {
    var handleResize = function handleResize() {
      if (window.innerWidth < 416 && !compact) setCompact(true);else if (window.innerWidth > 416 && compact) setCompact(false);
    };

    window.addEventListener('resize', handleResize);
    return function () {
      window.removeEventListener('resize', handleResize);
    };
  }, [compact]);
  useEffect(function () {
    if (toggled && fromDateRef.current && hasFilterBehavior) fromDateRef.current.focus();
  }, [hasFilterBehavior, toggled]);
  useEffect(function () {
    if (!startDate && !endDate) {
      updateTo(null);
      updateEnteredTo(null);
      updateFrom(null);
      updatePresetUsed(false);

      if (hasFilterBehavior) {
        setCount(0);
      }
    } else if (startDate && endDate) {
      if (hasFilterBehavior) {
        setCount(1);
      }
    }
  }, [startDate, endDate, hasFilterBehavior]);
  useEffect(function () {
    updateToInputField(to ? formatDate(to) : '');
    updateToInputFieldError('');
    updateFromInputField(from ? formatDate(from) : '');
    updateFromInputFieldError('');
  }, [from, to]);
  useEffect(function () {
    if (updateCount) {
      updateCount();
    }
  }, [_count, updateCount]);
  useImperativeHandle(ref, function () {
    return {
      count: function count() {
        return _count;
      }
    };
  });

  function apply() {
    onChange({
      startDate: from,
      endDate: to,
      presetUsed: presetUsed
    });

    if (toggled) {
      toggle();
    }
  }

  function clear() {
    updateTo(null);
    updateEnteredTo(null);
    updateFrom(null);
    updatePresetUsed(false);
    onChange({
      startDate: null,
      endDate: null,
      presetUsed: false
    });

    if (toggled) {
      toggle();
    }
  }

  function presetSelected(_a) {
    var startDate = _a.startDate,
        endDate = _a.endDate;
    updateFrom(startDate);
    updateEnteredTo(endDate);
    updateTo(endDate);
    updatePresetUsed(true);
  }

  function isSelectingFirstDay(day) {
    var isBeforeFirstDay = from && DateUtils.isDayBefore(day, from);
    return !from || isBeforeFirstDay;
  }

  function handleDayClick(day) {
    updatePresetUsed(false);

    if (from && to) {
      updateTo(null);
      updateEnteredTo(null);
      updateFrom(day);
      return;
    }

    if (isSelectingFirstDay(day)) {
      updateFrom(day);
    } else {
      updateEnteredTo(day);
      updateTo(day);
    }
  }

  function handleDayMouseEnter(day) {
    if (!isSelectingFirstDay(day) && !to) {
      updateEnteredTo(day);
    }
  }

  function handleClickOutside() {
    if (toggled) {
      toggle();

      if (from && startDate && DateUtils.isSameDay(from, startDate) || to && endDate && DateUtils.isSameDay(to, endDate)) {
        updateFromInputField(startDate ? formatDate(startDate) : '');
        updateFromInputFieldError('');
        updateToInputField(endDate ? formatDate(endDate) : '');
        updateToInputFieldError('');
      } else {
        updateFrom(startDate);
        updateEnteredTo(endDate);
        updateTo(endDate);
      }

      updatePresetUsed(false);
    }
  }

  function fromDateFocus() {
    updateFromInputFieldError('');
  }

  function fromDateInputChanged(event) {
    updateFromInputField(autoCompleteDateString(event.target.value));
    updatePresetUsed(false);
  }

  function fromDateBlur(event) {
    var sanitizedString = sanitizeDateString(event.target.value);
    updateFromInputField(sanitizedString);
    var validationResult = validateDate(sanitizedString, hasFilterBehavior);

    if (validationResult === '' && sanitizedString !== '') {
      var newFromDate = dateFromDateString(sanitizedString);

      if (!to || newFromDate <= to) {
        updateFrom(newFromDate);
      } else {
        updateFromInputFieldError('fromAfterTo');
      }
    } else {
      updateFromInputFieldError(validationResult);
    }
  }

  function toDateFocus() {
    updateToInputFieldError('');
  }

  function toDateInputChanged(event) {
    updateToInputField(autoCompleteDateString(event.target.value));
    updatePresetUsed(false);
  }

  function toDateBlur(event) {
    var sanitizedString = sanitizeDateString(event.target.value);
    updateToInputField(sanitizedString);
    var validationResult = validateDate(sanitizedString, true);

    if (validationResult === '' && sanitizedString !== '') {
      var newToDate = dateFromDateString(sanitizedString);

      if (!from || newToDate >= from) {
        updateTo(newToDate);
        updateEnteredTo(newToDate);
      } else {
        updateToInputFieldError('toBeforeFrom');
      }
    } else {
      updateToInputFieldError(validationResult);
    }
  }

  var selectedDays = from ? [from, {
    from: from,
    to: enteredTo ? enteredTo : from
  }] : undefined;
  var modifiers = {
    start: nullToUndefined(from),
    end: nullToUndefined(to),
    single: function single() {
      return from && to ? DateUtils.isSameDay(from, to) : false;
    },
    future: {
      after: new Date()
    }
  };

  if (hidden) {
    return null;
  }

  return jsx(React.Fragment, null, jsx(Global, {
    styles: StyleOverrides
  }), jsx(Wrapper, null, jsx(Dropdown, {
    handle: jsx(DropdownHandle, {
      toggled: toggled,
      dateCount: _count,
      toggle: toggle,
      title: startDate && endDate ? printDate(startDate, language) + " - " + printDate(endDate, language) : label ? label : strings.dateRange.placeholder,
      id: id
    }),
    footer: jsx(DropdownFooter, {
      apply: apply,
      strings: strings,
      clear: clear,
      hasFilterBehaviour: hasFilterBehavior,
      dateRange: {
        startDate: startDate,
        endDate: endDate
      },
      id: id,
      disabled: !(from && to) || fromInputFieldError !== '' || toInputFieldError !== '' || fromInputField === '' || toInputField === ''
    }),
    toggled: toggled,
    onOutsideClick: handleClickOutside
  }, jsx(PresetSelector, {
    onClick: presetSelected,
    selectedRange: {
      startDate: from,
      endDate: to
    },
    presets: presets,
    compact: compact,
    strings: strings.dateRange.presetSelector
  }), jsx(CalendarContainer, null, jsx(InputsContainer, null, jsx(Input, {
    ref: fromDateRef,
    placeholder: strings.dateRange.start,
    size: "small",
    value: fromInputField,
    onFocus: fromDateFocus,
    onChange: fromDateInputChanged,
    onBlur: fromDateBlur,
    errorMessage: fromInputFieldError
  }), jsx(Icon, {
    className: "range-arrow",
    content: calendarRangeArrow_16
  }), jsx(Input, {
    placeholder: strings.dateRange.end,
    size: "small",
    value: toInputField,
    onFocus: toDateFocus,
    onChange: toDateInputChanged,
    onBlur: toDateBlur,
    errorMessage: toInputFieldError
  })), fromInputFieldError ? jsx(InputFieldError, null, strings.dateRange.start + ": " + strings.dateRange.validations[fromInputFieldError]) : null, toInputFieldError ? jsx(InputFieldError, null, strings.dateRange.end + ": " + strings.dateRange.validations[toInputFieldError]) : null, jsx(DayPicker, {
    showOutsideDays: true,
    className: "Range",
    numberOfMonths: 1,
    modifiers: modifiers,
    selectedDays: selectedDays,
    onDayClick: handleDayClick,
    onDayMouseEnter: handleDayMouseEnter,
    navbarElement: NavBar
  })))));
});
export default DateRange;


//////////////////
// WEBPACK FOOTER
// ./node_modules/@trustpilot/businessapp-patternlibrary/lib/components/Header/filters/daterange/index.js
// module id = 1029
// module chunks = 0